/**
 * Bosnian strings for the analytics screens.
 *
 * The base every other language is measured against. Enum labels are NOT
 * here and must never be — statuses, types, priorities, roles and months
 * come from /api/meta, and a second copy is the drift that put IN_PROGRESS
 * on a manager's badge in the legacy app.
 *
 * This screen is the one where that rule is easiest to break, because almost
 * every axis, legend and table cell on it is a label the server sent:
 * `timeInStatus[].label` is a status, `byType[].label` is a type,
 * `backlogAge[].label` and `reportingRhythm.weekdays[].label` are buckets and
 * weekday names, `period` is a month. None of them appear below. What does
 * appear is the furniture around them — the tile captions, the sentence under
 * each chart explaining what it is for, and the series names this client
 * chooses for itself.
 *
 * A few captions here read like statuses — "Riješeno", "Otvoreno" — and are
 * not. They are the names of the figures this page computes over a period,
 * they are not keyed to anything, and nothing renders a submission's status
 * from them.
 */

export const analytics = {
  // ---- Page chrome -------------------------------------------------
  'analytics.title': 'Analitika',
  'analytics.subtitle': 'Pregled prijava, rješavanja i 5S rezultata',
  'analytics.period.label': 'Period',

  /*
   * One whole phrase per range rather than "Zadnj… {count} …".
   *
   * The number decides both words around it: 3 takes *Zadnja 3 mjeseca* and 12
   * takes *Zadnjih 12 mjeseci*. A single template with a placeholder can only
   * be right for one of them, and English collapses the pair into one form
   * anyway — so each range is written out, in each language, once.
   */
  'analytics.range.days30': 'Zadnjih 30 dana',
  'analytics.range.months3': 'Zadnja 3 mjeseca',
  'analytics.range.months12': 'Zadnjih 12 mjeseci',

  'analytics.export': 'Izvezi analitiku',

  // ---- KPI tiles ---------------------------------------------------
  'analytics.kpi.label': 'Ključni pokazatelji',
  'analytics.kpi.created': 'Nove prijave',
  'analytics.kpi.resolved': 'Riješeno',
  'analytics.kpi.openNow': 'Otvoreno sada',
  'analytics.kpi.openNow.hint': '{count} van roka',
  'analytics.kpi.avgResolution': 'Prosječno rješavanje',
  'analytics.kpi.fiveS': '5S prosjek',
  'analytics.kpi.fiveS.hint': 'posljednja ocjena po centru',
  'analytics.kpi.improvements': 'Prijedlozi',
  'analytics.kpi.improvements.hint': '{count} prihvaćeno',
  'analytics.kpi.acceptance': 'Prihvaćenost prijedloga',
  'analytics.kpi.awards': 'Isplaćene nagrade',

  // ---- Series names ------------------------------------------------
  // What the legend and the tooltip call each line and bar. The client picks
  // these because the client picks the series; the categories they are plotted
  // against still come from the server.
  'analytics.series.reported': 'Prijavljeno',
  'analytics.series.resolved': 'Riješeno',
  'analytics.series.total': 'Ukupno',
  'analytics.series.open': 'Otvoreno',
  'analytics.series.overdue': 'Van roka',
  'analytics.series.submissions': 'Prijava',
  'analytics.series.proposals': 'Prijedloga',

  // ---- Volume and breakdown charts ---------------------------------
  'analytics.overTime.title': 'Prijave kroz vrijeme',
  'analytics.overTime.empty': 'Nema podataka za izabrani period',
  'analytics.byType.title': 'Po vrsti prijave',
  'analytics.empty.noSubmissions': 'Nema prijava',
  'analytics.timeInStatus.title': 'Prosječno vrijeme po statusu',
  'analytics.timeInStatus.hint':
    'Pokazuje gdje se prijave zadržavaju — najduži stubić je usko grlo procesa.',
  /*
   * What the sentence above does not say, and what changes how the chart is
   * read: a stay is measured only once it ends. The status a prijava is sitting
   * in right now contributes nothing, so the bar for a status everything is
   * stuck in stays short exactly while the problem is at its worst.
   */
  'analytics.timeInStatus.explain':
    'Vrijeme se broji tek kada prijava izađe iz statusa, pa ono što u njemu još stoji ne ulazi ni u jedan stubić.',
  'analytics.timeInStatus.empty': 'Još nema promjena statusa',
  'analytics.timeInStatus.empty.description':
    'Podaci se pojavljuju kada prijave počnu da mijenjaju status.',
  'analytics.timeInStatus.tooltip': 'Prosjek',
  'analytics.byWorkCenter.title': 'Prijave po radnom centru',

  // ---- Process analysis --------------------------------------------
  'analytics.process.title': 'Analiza procesa',
  'analytics.process.firstResponse': 'Do prve reakcije',
  'analytics.process.firstResponse.hint': 'Koliko prijava čeka da je neko preuzme',
  'analytics.process.resolution': 'Do rješenja',
  // The status is named in prose, not rendered from here — the badge on a
  // submission still gets its wording from /api/meta.
  'analytics.process.resolution.hint': 'Od prijave do statusa „riješeno”',
  'analytics.process.accepted': 'Prihvaćeno prijedloga',
  'analytics.process.accepted.hint': '{count} čeka odluku',
  'analytics.process.implemented': 'Sprovedeno od prihvaćenih',
  'analytics.process.implemented.hint': 'Prihvaćeno pa nikad urađeno gasi motivaciju',

  // ---- Backlog age -------------------------------------------------
  'analytics.backlog.title': 'Starost otvorenih prijava',
  'analytics.backlog.hint':
    '„42 otvorene” ne govori ništa. Koliko ih je starije od sedmice — govori.',
  // The one chart on this page that ignores the period picker above it: the
  // backlog is a present-tense fact, and a reader who has just chosen "zadnjih
  // 30 dana" has every reason to assume otherwise.
  'analytics.backlog.explain':
    'Mjeri se od dana prijave do danas i obuhvata sve otvorene prijave, a ne samo one iz izabranog perioda.',
  'analytics.backlog.empty': 'Nema otvorenih prijava',

  // ---- Improvement funnel ------------------------------------------
  'analytics.funnel.title': 'Put prijedloga za poboljšanje',
  'analytics.funnel.hint':
    'Od predloženog do sprovedenog — i koliko je od procijenjene uštede zaista ostvareno.',
  'analytics.funnel.empty': 'Nema prijedloga u ovom periodu',
  'analytics.funnel.estimatedSavings': 'Procijenjena ušteda',
  'analytics.funnel.actualSavings': 'Ostvarena ušteda',
  'analytics.funnel.estimatedCost': 'Procijenjeni trošak',
  'analytics.funnel.rejected': 'Odbijeno',

  // ---- Reporting rhythm --------------------------------------------
  'analytics.rhythm.title': 'Kada stižu prijave',
  'analytics.rhythm.hint':
    'Prati smjene. Smjena koja ne prijavljuje ništa ili nema kvarova — ili ne prijavljuje.',

  // ---- Assignee load -----------------------------------------------
  'analytics.assignees.title': 'Opterećenje zaduženih',
  'analytics.assignees.hint':
    'Deset otvorenih od kojih nijedna nije kasna je zdravo. Tri, od kojih su dvije kasne, nije.',
  'analytics.assignees.empty': 'Nema zaduženih otvorenih prijava',

  // ---- Recurring issues --------------------------------------------
  'analytics.recurring.title': 'Šta se ponavlja',
  // "Hoblanje" stays a work centre in every language. It is the name of a real
  // machine on this floor and the reader will see it on the chart beside this
  // sentence, so translating the example would break the link it is making.
  'analytics.recurring.hint':
    'Radni centar i vrsta zajedno. „Hoblanje ima 40 prijava” je šum; „Hoblanje ima 19 kvarova” je mašina koju treba pogledati.',
  'analytics.recurring.empty': 'Nema podataka za ovaj period',

  // ---- Top contributors --------------------------------------------
  'analytics.contributors.title': 'Najaktivniji zaposleni',
  'analytics.contributors.hint':
    'Ko najviše prijavljuje i koliko je poena osvojio u ovom periodu.',
  'analytics.contributors.empty': 'Nema prijava u ovom periodu',
  'analytics.contributors.unit': 'prijava',

  // ---- 5S ----------------------------------------------------------
  'analytics.fiveS.title': '5S rezultati',
  'analytics.fiveS.year': 'Godina',
  'analytics.fiveS.missing.title': 'Radni centri bez ocjene ovog mjeseca',
  'analytics.fiveS.missing.hint':
    'Visok prosjek na dijelu centara nije dobar mjesec — ocijenite i preostale.',
  'analytics.fiveS.trend.title': 'Trend po radnom centru',
  // "Montaža" is a work centre too, and named for the same reason as above: the
  // 1–3 scale is that centre's, and the reader has to be able to find it.
  'analytics.fiveS.trend.hint':
    'Prikazano u procentima. Montaža se ocjenjuje 1–3, ostali centri 1–5, pa sirovi prosjeci nisu uporedivi.',
  'analytics.fiveS.byWorker.title': 'Trend po radniku',
  'analytics.fiveS.byWorker.hint':
    'Prosjek radnih centara koje je radnik održavao, po mjesecu. Ocjenjuje se centar, ne radnik — neko ko je preuzeo zapušten centar imaće nizak procenat upravo zato što je poslan da ga sredi.',
  'analytics.fiveS.byWorker.empty': 'Nema podataka po radnicima',
  'analytics.fiveS.byWorker.empty.description':
    'Radnici se biraju pri unosu 5S evaluacije. Evaluacije bez upisanih radnika se ovdje ne prikazuju.',

  /*
   * Three whole sentences behind one `tp()` stem, not one sentence with a noun
   * glued on the end.
   *
   * The count changes the verb as well as the noun — *jedan radnik nije
   * prikazan*, *dva radnika nisu prikazana*, *pet radnika nije prikazano* — so
   * there is no seam in the middle where a translator could substitute a word
   * and be finished. English needs two of the three and repeats itself; that is
   * cheaper than a second selector nobody would remember to keep in step.
   *
   * `{count}` is the number hidden, which is the one the form is chosen from;
   * `{shown}` is passed alongside it.
   */
  'analytics.fiveS.workers.hidden.one':
    'Prikazano {shown} radnika sa najviše evaluacija; još {count} radnik nije prikazan.',
  'analytics.fiveS.workers.hidden.few':
    'Prikazano {shown} radnika sa najviše evaluacija; još {count} radnika nisu prikazana.',
  'analytics.fiveS.workers.hidden.many':
    'Prikazano {shown} radnika sa najviše evaluacija; još {count} radnika nije prikazano.',

  'analytics.fiveS.byCategory.title': 'Prosjek po kategoriji',
  // The percentage is not a presentation choice, it is what makes the bars
  // comparable at all. Said again here rather than only under the trend chart:
  // this is a different card, and the two are read hours apart.
  'analytics.fiveS.byCategory.explain':
    'Prosjek svih odgovora u kategoriji za izabranu godinu, preračunat u procenat skale obrasca — Montaža se ocjenjuje 1–3, ostali centri 1–5, pa sirove ocjene ne stoje na istoj osi.',
  'analytics.fiveS.noEvaluations': 'Nema predatih evaluacija',
  'analytics.fiveS.worst.title': 'Kriterijumi sa najslabijom ocjenom',
  /*
   * What the table is sorted by, and the caveat that goes with it.
   *
   * The count behind each average is computed but not shown, so a criterion
   * scored once outranks one scored fifty times on a single bad answer. Naming
   * that is the difference between a work order and a list somebody quietly
   * stops trusting.
   */
  'analytics.fiveS.worst.explain':
    'Poređano po prosjeku za cijelu godinu, od najnižeg — kriterijum ocijenjen jednom stoji ovdje pored onog ocijenjenog pedeset puta.',
  'analytics.fiveS.worst.empty': 'Nema predatih evaluacija za ovu godinu',
  'analytics.fiveS.worst.criterion': 'Kriterijum',
  'analytics.fiveS.worst.category': 'Kategorija',
  'analytics.fiveS.worst.template': 'Obrazac',
  'analytics.fiveS.worst.average': 'Prosjek',

  // ---- Pager -------------------------------------------------------
  // DataPager is shared rather than analytics-only, but its strings are one
  // component's rather than the whole application's, so they live beside the
  // screen that owns the file instead of in common.ts.
  'analytics.pager.empty': 'Nema zapisa',
  'analytics.pager.range': '{from}–{to} od {total}',
  'analytics.pager.perPage': '{count} po stranici',
  'analytics.pager.perPage.label': 'Redova po stranici',
  'analytics.pager.prev': 'Prethodna stranica',
  'analytics.pager.next': 'Sljedeća stranica',

  // ---- 5S by shift -------------------------------------------------
  'analytics.shifts.title': 'Po smjenama',
  // Says what the section is *for*. A chart titled "po smjenama" invites the
  // reader to rank the shifts; this sentence says what the ranking would mean.
  'analytics.shifts.subtitle':
    'Da li je bitno ko je na smjeni. Ako sve smjene drže mašinu jednako, problem je u mašini; ako je razlika velika, problem je u smjeni.',
  // Why the section can be missing altogether. Everything below is built from
  // walks that name a shift, and a reader who has never seen it has no way to
  // tell "nothing to show" from "not turned on".
  'analytics.shifts.explain':
    'Sve ovdje se gradi iz obilazaka na kojima je izabrana smjena; dok smjene ne postoje ili ih obilasci ne nose, nema se šta prikazati.',
  // Two nouns, two counts, so two stems: `tp()` picks the form from one
  // number, and "3 centara" is as wrong as "5 centar".
  'analytics.shifts.walkCount.one': '{count} obilazak',
  'analytics.shifts.walkCount.few': '{count} obilaska',
  'analytics.shifts.walkCount.many': '{count} obilazaka',
  'analytics.shifts.centreCount.one': '{count} centar',
  'analytics.shifts.centreCount.few': '{count} centra',
  'analytics.shifts.centreCount.many': '{count} centara',
  'analytics.shifts.thin': 'malo podataka',
  'analytics.shifts.unassigned.one':
    '{count} evaluacija nije vezana ni za jednu smjenu, pa nije uključena u prikaz iznad.',
  'analytics.shifts.unassigned.few':
    '{count} evaluacije nisu vezane ni za jednu smjenu, pa nisu uključene u prikaz iznad.',
  'analytics.shifts.unassigned.many':
    '{count} evaluacija nije vezano ni za jednu smjenu, pa nisu uključene u prikaz iznad.',
  'analytics.shifts.spread.title': 'Gdje je razlika među smjenama najveća',
  'analytics.shifts.spread.hint':
    'Mašine koje jedna smjena drži bitno bolje od druge. Prosjek to skriva — ovdje se vidi.',
  // What the number is the difference *between*, and why a machine somebody
  // expects to see is not in the list: a gap needs two sides, so a centre only
  // one shift walked cannot have one.
  'analytics.shifts.spread.explain':
    'Razlika je između prosjeka najbolje i najslabije smjene na tom centru, a centar ulazi u spisak tek kada su ga u periodu obišle najmanje dvije smjene.',
  'analytics.shifts.spread.gap': 'razlika {gap}',
  'analytics.shifts.trend.title': 'Kretanje po smjenama',
  'analytics.shifts.categories.title': 'Šta koja smjena najslabije drži',
  'analytics.shifts.categories.hint':
    'Ocjene su preračunate u procente, pa se Montaža (skala 1–3) poredi sa ostalim centrima (1–5).',

  // ---- Per worker --------------------------------------------------
  'analytics.workforce.title': 'Po radniku',
  // The warning goes above the table rather than under it: it is what somebody
  // needs before they draw a conclusion, not after.
  'analytics.workforce.hint':
    'Tri odvojene stvari, namjerno bez zbira. 5S govori o mašinama koje je neko držao, a ne o njemu; prijave govore o inicijativi; zadaci i o tome koliko je posla nekome dodijeljeno. Jedan zbirni broj bi se čitao kao ocjena radnika.',
  /*
   * The two things the printed formula above the table leaves out.
   *
   * It says the weights and it says a proposal can only raise a rating; it does
   * not say what happens to somebody who has nothing in one of the two halves.
   * A new starter with no walks is rated on tasks alone at full scale, and
   * saying so is what stops the table being read as "they failed 5S". The 5S
   * caveat itself is not repeated here — it is the first thing in the sentence
   * above this note.
   */
  'analytics.workforce.rating.note':
    'Prihvaćeni prijedlozi se dodaju na osnovu i mogu je samo podići — odbijen prijedlog ne oduzima ništa. Ko nije bio ni na jednom obilasku ili nema nijedan zadatak, ocjenjuje se samo po onome što ima, a dio koji nedostaje se ne računa kao nula.',
  'analytics.workforce.undated.one':
    '{count} evaluacija nema upisan datum, pa ne ulazi ni u jedan period.',
  'analytics.workforce.undated.few':
    '{count} evaluacije nemaju upisan datum, pa ne ulaze ni u jedan period.',
  'analytics.workforce.undated.many':
    '{count} evaluacija nema upisan datum, pa ne ulazi ni u jedan period.',
  'analytics.workforce.person': 'Radnik',
  'analytics.workforce.fiveS': '5S',
  // The small coloured number beside the percentage is nowhere explained on
  // screen, and a delta against an unnamed baseline is worse than no delta.
  'analytics.workforce.fiveS.hint':
    'Prosjek svih obilazaka na kojima je radnik upisan; manji broj pored njega je razlika u odnosu na prethodni period iste dužine.',
  'analytics.workforce.walks': 'Obilasci',
  // `{walks}` is the threshold the server publishes with the formula, not a
  // number written here — the marking and its explanation have to move together.
  'analytics.workforce.walks.hint':
    'Broj predatih 5S obilazaka na kojima je radnik upisan; ispod {walks}, ocjena nosi oznaku da počiva na malo podataka.',
  'analytics.workforce.filed': 'Prijave',
  'analytics.workforce.accepted': 'Prihvaćene',
  // Which of the three report columns actually moves the rating. Volume does
  // not, and somebody who assumes it does files noise to climb the list.
  'analytics.workforce.accepted.hint':
    'U ocjenu ulazi samo ovaj broj — koliko je prijava poslato i koliko je bodova osvojeno ne mijenja je.',
  'analytics.workforce.points': 'Bodovi',
  'analytics.workforce.tasks': 'Zadaci',
  // Both numbers describe the same set, which is the whole reason they are
  // printed as a pair: a backlog cleared this week does not belong in either.
  'analytics.workforce.tasks.hint':
    'Završeni od onih koji su zaduženi u ovom periodu; zadaci iz ranijih perioda ne ulaze ni u jedan od ta dva broja.',
  'analytics.workforce.overdue': 'Kasni',
  'analytics.workforce.doneOf': '{done} / {total}',
  'analytics.shifts.month': 'Mjesec',
  'analytics.shifts.wholeYear': 'Cijela godina',
  // Shown only to an administrator, and only while no shift exists: every
  // other part of the feature is invisible until one does, and the first
  // person to look for it could not find where to start.
  'analytics.shifts.none.title': 'Smjene još nisu definisane',
  'analytics.shifts.none.description':
    'Kada dodate smjene, evaluator ih bira pri 5S obilasku, a ovdje se pojavljuje poređenje po smjenama — koja smjena drži koju mašinu bolje.',
  'analytics.shifts.none.action': 'Dodaj smjene',
  'analytics.shifts.unused.title': 'Nijedna evaluacija još nije vezana za smjenu',
  'analytics.shifts.unused.description':
    'Smjene su definisane. Poređenje se pojavljuje nakon prvog 5S obilaska na kojem je izabrana smjena — ranije evaluacije ostaju bez nje.',
  'analytics.shifts.unused.action': 'Novi 5S obilazak',
  'analytics.workforce.search': 'Ime ili šifra zaposlenog',
  // Who is in the table before anybody narrows it. The default is a roll call,
  // and that is the point — a table that omits the quiet ones cannot be used to
  // notice them.
  'analytics.workforce.search.hint':
    'U tabeli su svi aktivni zaposleni, i oni bez ijednog podatka u periodu, da bi se i tihi mogli primijetiti.',
  'analytics.workforce.role': 'Uloga',
  'analytics.workforce.allRoles': 'Sve uloge',
  // Nobody is scored differently for being a supervisor, which is not obvious
  // from a dropdown that mixes operators and managers in one ranking.
  'analytics.workforce.role.hint':
    'Uloga ne mijenja način računanja ocjene — svi se računaju po istoj formuli, pa ovaj izbor samo sužava spisak.',
  'analytics.workforce.workCenter': 'Radni centar',
  'analytics.workforce.allCentres': 'Svi radni centri',
  /*
   * Where the "the rank does not move" promise is made to the reader.
   *
   * This is the filter that most invites the wrong reading: narrow to one
   * machine, see #4 at the top of the list, and conclude the person is fourth
   * on that machine. They are fourth in the plant — the ranking is worked out
   * before anything is filtered away, deliberately.
   */
  'analytics.workforce.workCenter.hint':
    'Bira ljude raspoređene na taj centar; redni broj pored ocjene ostaje mjesto u cijelom pogonu, jer se rang računa prije filtera.',
  'analytics.workforce.ratedOnly': 'Samo ocijenjeni',
  // The default is a roll call, so the quiet ones can be noticed; the filter
  // turns it into a rang lista.
  'analytics.workforce.ratedOnly.hint':
    'Sakrij one za koje nema dovoljno podataka za ocjenu.',
  'analytics.workforce.empty': 'Nema radnika koji odgovaraju filterima.',
} as const

export type AnalyticsSlice = Record<keyof typeof analytics, string>
